import { useEffect, useState } from 'react';

import { debounce } from 'debounce';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { useSbPlayerCb } from 'components/app/storyboard/sb-player/_mediator/use-sb-player-cb';
import { useSbPlayerContext } from 'components/app/storyboard/sb-player/_mediator/sb-player.provider';
import { isLoaderActive } from 'components/app/storyboard/sb-player/_mediator/sb-player.helper';
import { setReadyPlayersListener, resetStoryboard, setErrorRate, setPanZoomControl } from './sb.slice';
import { useSbContext } from './sb.provider';
import { useSbCb } from './use-sb-cb';
import { useSbLogger } from './use-sb-logger';

export const useSb = () => {
  const { isAudioPlaying } = useAppSelector((state) => state.audio);
  const { selectedStory } = useAppSelector((state) => state.projects);
  const { clipsMap, storyConfig, markers, configPanel, panZoomKeyFrameIndex } = useAppSelector((state) => state.editor);
  const { sbPlayback, sbWsPlayback, sbClipPlaybackMap, readyPlayersListener, errorRateMap, panZoomControl } =
    useAppSelector((state) => state.sb);
  const { playbackClips, activePlayerIndex } = useAppSelector((state) => state.sb__player);
  const tracksRestored = useAppSelector((state) => state.memory.tracksRestored);
  const storyLoaded = useAppSelector((state) => state.app.storyLoaded);
  const dragger = useAppSelector((state) => state.app.dragger);

  const { readyToPlay } = useSbPlayerContext();
  const { sbListenClip, sbListenSkipDamping } = useSbContext();
  const { isWsFinished, wsGetCursorTime, wsPlay, wsSeekTo } = useAudioTrackContext();
  const { sbPlay, sbPause, sbSeekTo, sbSelectClip, sbSync } = useSbCb();
  const { getPlaybackReadyPlayers } = useSbPlayerCb();
  const logger = useSbLogger();
  const dispatch = useAppDispatch();

  const [listenReadyPlayers, setListenReadyPlayers] = useState(false);

  useEffect(() => {
    if (!clipsMap) return;
    Object.keys(errorRateMap).forEach((clipId) => {
      if (!clipsMap[clipId]) dispatch(setErrorRate({ clipId }));
    });
  }, [clipsMap]);

  /* pan-zoom */

  useEffect(() => {
    if (configPanel === 'clip') return;
    dispatch(setPanZoomControl(false));
    if (panZoomKeyFrameIndex) {
      sbSeekTo(wsGetCursorTime());
      sbListenClip.current = null;
    }
  }, [configPanel]);

  /* seek */

  useEffect(() => {
    if (!storyLoaded || !tracksRestored) return;
    if (sbListenClip.current) {
      const { clipId, track, cell } = sbListenClip.current;
      sbSelectClip(clipId, cell, track);
    } else {
      sbListenSkipDamping.current = true;
      sbSeekTo(wsGetCursorTime());
    }
  }, [sbClipPlaybackMap, storyConfig]);

  /* playback */

  const cb = () => {
    if (dragger !== null) return;
    if (isLoaderActive(readyToPlay.current, playbackClips, activePlayerIndex)) return;
    const players = getPlaybackReadyPlayers();
    logger?.play(players);
    sbPlay(players, 'onSeek');
  };

  useEffect(() => {
    if (sbPlayback) {
      if (typeof storyConfig?.finishMarkerId === 'number') {
        const time = markers![storyConfig.finishMarkerId];
        if (isWsFinished(time)) {
          sbSeekTo(time);
          return;
        }
      }
      if (panZoomControl) {
        dispatch(setPanZoomControl(false));
        sbSeekTo(wsGetCursorTime());
        return;
      }
      if (isWsFinished()) sbSeekTo(0);
      else sbPlay(getPlaybackReadyPlayers(), 'onSeek');
    } else sbPause();
  }, [sbPlayback]);

  useEffect(() => {
    wsPlay(sbWsPlayback);
    if (isWsFinished()) wsSeekTo(0);
    return () => {
      if (sbWsPlayback) sbSync();
    };
  }, [sbWsPlayback]);

  useEffect(() => {
    if (dragger !== null) return;
    dispatch(setReadyPlayersListener());
  }, [dragger]);

  useEffect(() => {
    setListenReadyPlayers(sbPlayback && !isAudioPlaying);
  }, [sbPlayback, isAudioPlaying]);

  useEffect(() => {
    if (!listenReadyPlayers) return;
    debounce(cb, 'readyPlayersListener', 11);
  }, [readyPlayersListener, listenReadyPlayers]);

  /* reset */

  useEffect(() => {
    return () => {
      dispatch(resetStoryboard());
    };
  }, [selectedStory]);
};
